import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent } from '@shopify/events';

// Some components overlap their neighbours slightly so the stack looks
// continuous rather than like a column of separate blocks.
const KNOT_OVERLAP_PX = 3;
const TAIL_OVERLAP_PX = 3;
const SPACER_OVERLAP_PX = 1.5;
const TOPLOOP_OVERLAP_PX = 3;
// Width conventions (real-world cm). Images are authored so the actual object
// fits inside these widths; the rendered layer uses these fixed widths rather
// than deriving them from the image aspect ratio.
const OBJECT_WIDTH_CM = 3;
const KNOT_WIDTH_CM = 1.16;
const BALL_WIDTH_CM = 1.5;
// Top-loop geometry. The loop sprite (ropes.webp) is authored at 100px = 1cm:
// one 30px-wide braid frame per rope colour in a single row, 844px tall. The
// metal rings are square single images with a rope braid passing in front of
// their lower half. Per-style widths come from item widthCm (braid 0.5cm,
// rings 0.53cm); only the top portion of the full 8cm art is revealed and the
// height is user-adjustable.
// Ring horizontal nudge as a fraction of the ring's own width (negative =
// left) — user-calibrated to centre the ring over the braid by eye.
const TOPLOOP_RING_OFFSET_PCT = -0.03;
const TOPLOOP_FULL_HEIGHT_CM = 8;
const TOPLOOP_MIN_HEIGHT_CM = 0.7;
const TOPLOOP_DEFAULT_HEIGHT_CM = 1;
const TOPLOOP_DEFAULT_WIDTH_CM = 0.5;
// The 30:844 braid frame is unreadable as a whole (~4px wide at thumb size),
// so toploop sprite thumbs crop a window (art px) from the top of the frame:
// the head plus the first braid repeat.
const TOPLOOP_ART_FRAME_W_PX = 30;
const TOPLOOP_ART_FRAME_H_PX = 844;
const TOPLOOP_THUMB_WINDOW_PX = 150;
// Stacking order (highest first): bead over spacer over toploop/tail over knot.
const ROLE_Z = { beads: 4, decoration: 3, toploop: 2, tail: 2, knot: 1 };
// Modal titles carry a longer hint than the short group labels used in the
// left options column (rendered uppercase by CSS).
const MODAL_TITLES = {
  rope: 'Color (Pick Paracord Color)',
  toploop: 'Top Loop (Choose Loop Style & Height)',
  decoration: 'Spacer (Add a Spacer to Your Lanyard)',
  beads: 'Bead (Add a Bead to Your Lanyard)',
  tail: 'Tail (Choose Tail Style)',
};
// Modal groups rendered as a tall 3-column, vertically scrolling grid.
const GRID_MODAL_ROLES = new Set(['beads', 'rope', 'decoration']);

// URL hash encoding for shareable configurations, e.g. #C3TO1S1K1K1K1B2S2TA1.
// Code -> role. Segment order = stack order top to bottom (rope is the
// background and never enters the stack). Two scalar segments may follow the
// item segments: H<mm base36> encodes the top-loop height (omitted at the
// default 1cm) and L<ab> encodes a letter tail's two letters (omitted unless
// a letter tail is selected). Uppercase H/L never occur inside base36 values,
// so the item parser simply ignores them.
const HASH_CODE_TO_ROLE = { C: 'rope', TO: 'toploop', K: 'knot', S: 'decoration', B: 'beads', TA: 'tail' };
const HASH_ROLE_TO_CODE = Object.fromEntries(Object.entries(HASH_CODE_TO_ROLE).map(([code, role]) => [role, code]));
// Free roles (rope/knot/toploop) are hardcoded config, so their segment number
// is the 1-based index within the group. Paid roles come from Shopify products;
// their segment is the variantId in base36 (e.g. B3f5a2), so re-ordering,
// adding or removing variants/products in the admin never invalidates a
// shared link. Legacy all-digit segments for paid roles resolve to tiny
// variant ids that no longer exist and are simply dropped.
const VARIANT_HASH_ROLES = new Set(['beads', 'decoration', 'tail']);
// Configuration applied on a first visit (no saved state, no share hash).
// Roles whose segments no longer resolve fall back to their group's first
// item (see #applyDefaults).
const DEFAULT_CONFIG_HASH = 'C6TO1Sgbg3vnn7gK1K1K1Bgcuey3wakTA1';

/**
 * Build-your-own lanyard configurator.
 *
 * Reads component data serialized by sections/custom-lanyard-builder.liquid,
 * renders a selectable UI + a layered live preview, and adds the selected
 * components to the cart as multiple line items (so the total is the natural
 * cart sum plus a fixed assembly-fee product).
 *
 * @extends Component<{
 *   options: HTMLElement,
 *   stage: HTMLElement,
 *   stack: HTMLElement,
 *   total: HTMLElement,
 *   totalLabel: HTMLElement,
 *   addButton: HTMLButtonElement,
 *   addButtonText: HTMLElement,
 *   message: HTMLElement,
 * }>
 */
class LanyardBuilder extends Component {
  requiredRefs = ['options', 'stage', 'stack', 'total', 'addButton', 'addButtonText', 'message'];

  /** @type {number} */
  #uidCounter = 0;
  /** @type {Map<string, number>} sprite URL -> frame aspect ratio (w/h) */
  #spriteAspects = new Map();
  /** @type {Map<string, number>} single image URL -> aspect ratio (w/h) */
  #imageAspects = new Map();
  /** @type {Map<string, string>} rope image URL -> cropped strip data URL */
  #ropeCache = new Map();
  /** True while a hash-driven configuration is being applied, so render()
   *  does not immediately write the same hash back to the address bar. */
  #suppressHashSync = false;
  /** True while an add-to-cart request is in flight. */
  #adding = false;
  /** Letter-tail item awaiting letter confirmation in the picker. */
  #letterPendingItem = null;
  #resetModalScroll = false;
  #waveTimer = null;

  #onHashChange = () => this.#applyHashFromLocation();

  constructor() {
    super();
    /** @type {any} */
    this.data = null;
    /** @type {Array<{uid: number, role: string, item: any}>} */
    this.entries = [];
    /** @type {number[]} stackable entry uids in top-to-bottom order */
    this.stack = [];
    /** @type {string} conditional tail input value */
    this.tailInput = '';
    /** @type {string} two letters chosen for a letter-style tail (e.g. 'AB') */
    this.tailLetters = '';
    this.letterDraft = [null, null];
    this.letterSlot = 0;
    /** @type {number} reveal height (cm) of the top loop */
    this.toploopHeight = TOPLOOP_DEFAULT_HEIGHT_CM;
    this.moneyFormat = '${{amount}}';
    this.currency = 'USD';
    /** @type {string | null} role currently shown in the choice modal */
    this.openGroupRole = null;
    /** @type {HTMLElement | null} */
    this.modal = null;
    /** @type {number | null} uid of the currently selected stack item */
    this.selectedUid = null;
    /** @type {number | null} uid of the entry being swapped via the choice modal */
    this.swapTargetUid = null;
    /** @type {{uid: number, startY: number, offsetY: number, currentY: number, dragEl: HTMLElement, container: HTMLElement, isStack: boolean, slotH: number, moved: boolean} | null} */
    this.dragState = null;
    this.#suppressHashSync = false;
  }

  connectedCallback() {
    super.connectedCallback();

    const script = this.querySelector('[data-configurator-data]');
    if (script) this.data = JSON.parse(script.textContent);

    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';

    this.#indexRopeFrames();
    this.#applyDefaults();
    this.#loadState();

    // A valid configuration hash in the URL wins over defaults and
    // localStorage, and any later hash change re-applies the configuration.
    this.#applyHashFromLocation();
    window.addEventListener('hashchange', this.#onHashChange);

    this.refs.stage.addEventListener('click', (event) => {
      if (event.target === this.refs.stage) {
        this.selectedUid = null;
        this.render();
      }
    });

    const previewHint = this.querySelector('[data-preview-hint]');
    const previewHintClose = this.querySelector('.lanyard-builder__preview-hint-close');
    if (previewHint && sessionStorage.getItem('lanyard-builder-hint-closed') === '1') {
      previewHint.hidden = true;
    }
    if (previewHintClose && previewHint) {
      previewHintClose.addEventListener('click', () => {
        previewHint.hidden = true;
        sessionStorage.setItem('lanyard-builder-hint-closed', '1');
      });
    }

    const shareWrap = this.querySelector('.lanyard-builder__share-wrap');
    const shareBtn = this.querySelector('[data-share]');
    const shareMenu = this.querySelector('[data-share-menu]');
    if (shareWrap && shareBtn && shareMenu) {
      const closeMenu = () => {
        shareMenu.hidden = true;
        shareBtn.setAttribute('aria-expanded', 'false');
      };
      shareBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        const opening = shareMenu.hidden;
        closeMenu();
        shareMenu.hidden = !opening;
        shareBtn.setAttribute('aria-expanded', String(opening));
      });
      document.addEventListener('click', (event) => {
        if (!shareMenu.hidden && !shareWrap.contains(event.target)) closeMenu();
      });
      shareMenu.querySelector('[data-share-image]')?.addEventListener('click', () => {
        closeMenu();
        this.#shareImage();
      });
      shareMenu.querySelector('[data-download-image]')?.addEventListener('click', () => {
        closeMenu();
        this.#downloadImage();
      });
      shareMenu.querySelector('[data-share-link]')?.addEventListener('click', () => {
        closeMenu();
        this.#copyShareLink();
      });
    }

    // Import: paste a design hash (or a full share URL) to load that design.
    const importWrap = this.querySelector('.lanyard-builder__import-wrap');
    const importBtn = this.querySelector('[data-import]');
    const importPopover = this.querySelector('[data-import-popover]');
    const importInput = this.querySelector('[data-import-input]');
    const importGo = this.querySelector('[data-import-go]');
    if (importWrap && importBtn && importPopover && importInput && importGo) {
      const closeImport = () => {
        importPopover.hidden = true;
        importBtn.setAttribute('aria-expanded', 'false');
        importInput.removeAttribute('aria-invalid');
      };
      const runImport = () => {
        const raw = (importInput.value || '').trim();
        const hashPart = raw.includes('#') ? raw.slice(raw.indexOf('#') + 1) : raw;
        const cleaned = hashPart.replace(/^[#/]+/, '').trim();
        if (!cleaned || !this.#parseHash(cleaned)) {
          importInput.setAttribute('aria-invalid', 'true');
          importInput.select();
          return;
        }
        closeImport();
        // location.hash only fires hashchange on an actual change; re-apply
        // manually when the design is already the current one.
        if (window.location.hash.slice(1) === cleaned) {
          this.#applyHashFromLocation();
        } else {
          window.location.hash = `#${cleaned}`;
        }
      };
      importBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        const opening = importPopover.hidden;
        closeImport();
        importPopover.hidden = !opening;
        importBtn.setAttribute('aria-expanded', String(opening));
        if (opening) {
          importInput.value = '';
          importInput.focus();
        }
      });
      importPopover.addEventListener('click', (event) => event.stopPropagation());
      document.addEventListener('click', (event) => {
        if (!importPopover.hidden && !importWrap.contains(event.target)) closeImport();
      });
      importGo.addEventListener('click', runImport);
      importInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') runImport();
        if (event.key === 'Escape') closeImport();
        importInput.removeAttribute('aria-invalid');
      });
    }

    // Preset cards below the builder apply a hash config and smoothly scroll
    // back up to the builder when clicked.
    this.querySelectorAll('[data-preset-hash]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const hash = link.dataset.presetHash;
        if (!hash) return;
        history.replaceState(null, '', `#${hash}`);
        this.#applyHashFromLocation();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        requestAnimationFrame(() => this.#playRopeSway());
      });
    });

    // The stack wave plays after 10s without touching the builder: option
    // clicks, preview touches/clicks, and stack clicks/drags all count as
    // activity. Held-pointer movement covers long drags.
    const markActivity = () => this.#armIdleWave();
    this.addEventListener('click', markActivity);
    this.addEventListener('pointerdown', markActivity, { passive: true });
    this.addEventListener('keydown', markActivity);
    this.addEventListener('pointermove', (event) => {
      if (event.buttons) markActivity();
    });
    this.#armIdleWave();

    // On mobile the stage height comes from the layout (it matches the options
    // column) rather than an aspect-ratio, so re-layout the preview whenever its
    // size settles to keep the rope/layers centered correctly.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => this.renderPreview()).observe(this.refs.stage);
    }

    this.render();

    const loading = this.querySelector('[data-loading]');
    if (loading) {
      loading.classList.add('is-fading');
      setTimeout(() => {
        loading.hidden = true;
      }, 350);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.#onHashChange);
    clearTimeout(this.#waveTimer);
  }

  // -------------------------------------------------------------- rope sway
  // The whole lanyard swings left-right three times like a plucked rope,
  // pivoting from the top of the preview. Plays after 10s of inactivity and
  // when a preset build is applied.

  #armIdleWave() {
    clearTimeout(this.#waveTimer);
    this.#waveTimer = setTimeout(() => {
      this.#playRopeSway();
      this.#armIdleWave();
    }, 10000);
  }

  #playRopeSway() {
    if (document.hidden || this.openGroupRole) return;
    const stage = this.refs.stage;
    if (!stage || !stage.firstElementChild || typeof stage.animate !== 'function') return;

    // The stage itself carries the border/overflow, so the rope and layers
    // are temporarily re-parented into a full-size inner wrapper that swings
    // around the stage's top-centre, then moved back afterwards. A static
    // wrapper adds no containing block, so layer positioning is unaffected.
    const swing = document.createElement('div');
    swing.style.cssText = 'position: absolute; inset: 0; transform-origin: 50% 0;';
    while (stage.firstElementChild) swing.appendChild(stage.firstElementChild);
    stage.appendChild(swing);

    const restore = () => {
      while (swing.firstElementChild) stage.insertBefore(swing.firstElementChild, swing);
      swing.remove();
    };
    const animation = swing.animate(
      [
        { transform: 'rotate(0deg)' },
        { transform: 'rotate(4deg)' },
        { transform: 'rotate(-4deg)' },
        { transform: 'rotate(3.2deg)' },
        { transform: 'rotate(-3.2deg)' },
        { transform: 'rotate(2.2deg)' },
        { transform: 'rotate(-2.2deg)' },
        { transform: 'rotate(0deg)' },
      ],
      { duration: 1050, easing: 'ease-in-out' }
    );
    animation.onfinish = restore;
    animation.oncancel = restore;
  }

  // ------------------------------------------------------------------ utils

  get scale() {
    return this.data?.scale ?? 30;
  }

  get groups() {
    return this.data?.groups ?? [];
  }

  get labor() {
    return this.data?.labor ?? {};
  }

  #nextUid() {
    return ++this.#uidCounter;
  }

  #group(role) {
    return this.groups.find((g) => g.role === role);
  }

  #isStackable(role) {
    return role !== 'rope';
  }

  // Intentional overlap between a component and the next one below it, so the
  // stack looks continuous rather than like a column of separate blocks.
  #overlapPx(role) {
    return (
      { knot: KNOT_OVERLAP_PX, tail: TAIL_OVERLAP_PX, decoration: SPACER_OVERLAP_PX, toploop: TOPLOOP_OVERLAP_PX }[
        role
      ] || 0
    );
  }

  // Real-world height used for stacking and rendering. Knots and ball tails
  // ignore preview_height and derive their height from the fixed width ratio.
  #entryHeightCm(entry) {
    if (!entry) return 1;
    if (entry.role === 'knot') return KNOT_WIDTH_CM * 0.6;
    if (entry.role === 'tail' && entry.item.title?.toLowerCase().includes('ball')) return BALL_WIDTH_CM * 0.8;
    if (entry.role === 'toploop') return this.toploopHeight;
    return Number(entry.item.heightCm) || 1;
  }

  #entriesByRole(role) {
    return this.entries.filter((e) => e.role === role);
  }

  #entry(uid) {
    return this.entries.find((e) => e.uid === uid);
  }

  // Free components have no variantId, so identify them by title.
  #itemKey(item) {
    return item.variantId != null ? `v${item.variantId}` : `t${item.title}`;
  }

  #format(cents) {
    return formatMoney(Number(cents) || 0, this.moneyFormat, this.currency || 'USD');
  }

  #isSprite(item) {
    return Number(item.spriteColumns) > 0;
  }

  #currentRopeFrame() {
    const rope = this.#entriesByRole('rope')[0];
    if (!rope) return 0;
    const idx = Number(rope.item.frameIndex);
    return Number.isFinite(idx) ? idx : 0;
  }

  // Sprite frames follow the ascending numeric suffix of the rope image
  // filenames (paracord1.jpg = frame 0, …), NOT the display order of rope_data.
  // Assign each rope its rank in suffix order so gaps in the numbering still
  // map to consecutive frames.
  #indexRopeFrames() {
    const ropeGroup = this.groups.find((g) => g.role === 'rope');
    if (!ropeGroup) return;
    const sorted = [...ropeGroup.items].sort(
      (a, b) => (Number(a.frameSuffix) || 0) - (Number(b.frameSuffix) || 0)
    );
    sorted.forEach((item, index) => {
      item.frameIndex = index;
    });
  }

  // First visit (nothing in localStorage, no share hash): start from a curated
  // default combination instead of an empty lanyard. Roles whose segments no
  // longer resolve (e.g. variant ids gone after products were re-organized)
  // fall back to their group's first item, so the default is never partial.
  #applyDefaults() {
    const segments = this.#parseHash(DEFAULT_CONFIG_HASH) || [];
    for (const segment of segments) {
      this.#addEntry(segment.role, segment.item);
    }

    const rolesPresent = new Set(this.entries.map((e) => e.role));
    for (const group of this.groups) {
      if (!group.items || group.items.length === 0) continue;
      if (rolesPresent.has(group.role)) continue;
      if (group.quickAdd) continue;
      this.#addEntry(group.role, group.items[0]);
    }
  }

  #storageKey() {
    return `lanyard-builder-${this.dataset.sectionId || 'default'}`;
  }

  #saveState() {
    try {
      const state = {
        entries: this.entries,
        stack: this.stack,
        tailInput: this.tailInput,
        tailLetters: this.tailLetters,
        toploopHeight: this.toploopHeight,
        selectedUid: this.selectedUid,
      };
      localStorage.setItem(this.#storageKey(), JSON.stringify(state));
    } catch (error) {
      console.warn('[lanyard-builder] Failed to save state:', error);
    }
  }

  #loadState() {
    try {
      const raw = localStorage.getItem(this.#storageKey());
      if (!raw) return;
      const state = JSON.parse(raw);
      if (!state || !Array.isArray(state.entries) || !Array.isArray(state.stack)) return;

      // Make sure every saved item still exists in the current groups. Paid
      // items are matched by variantId, free items (rope/knot/toploop) by
      // title. Entries pointing at vanished variants are dropped rather than
      // kept with a dead variantId that would fail at add-to-cart.
      const allItems = this.groups.flatMap((g) => g.items || []);
      const findItem = (entry) => {
        if (!entry || !entry.item) return null;
        const wanted = entry.item;
        return (
          allItems.find((item) => {
            if (wanted.variantId != null) return item.variantId === wanted.variantId;
            return item.title === wanted.title;
          }) ?? null
        );
      };

      const restoredEntries = [];
      for (const entry of state.entries) {
        const item = findItem(entry);
        if (!item) continue;
        restoredEntries.push({ uid: entry.uid, role: entry.role, item });
      }

      if (restoredEntries.length === 0) return;

      this.entries = restoredEntries;
      this.stack = state.stack.filter((uid) => restoredEntries.some((e) => e.uid === uid));
      this.tailInput = state.tailInput || '';
      this.tailLetters = /^[A-Z]{2}$/.test(state.tailLetters || '') ? state.tailLetters : '';
      this.toploopHeight = Math.min(
        TOPLOOP_FULL_HEIGHT_CM,
        Math.max(TOPLOOP_MIN_HEIGHT_CM, state.toploopHeight ?? TOPLOOP_DEFAULT_HEIGHT_CM)
      );
      this.selectedUid = state.selectedUid ?? null;
      this.#uidCounter = Math.max(0, ...restoredEntries.map((e) => e.uid));
    } catch (error) {
      console.warn('[lanyard-builder] Failed to load state:', error);
    }
  }

  // ------------------------------------------------------------ URL hash

  // Parse a configuration hash like `#C3TO1S1K1K1K1B3f5a2TA1` into an ordered
  // list of {role, item}. Paid roles resolve their suffix as a base36
  // variantId, free roles as a 1-based item index. Returns null when nothing
  // valid is found, so the caller can leave the current configuration
  // untouched.
  #parseHash(hash) {
    if (!hash || hash.length < 2) return null;
    const segments = [];
    const regex = /(TO|TA|C|K|S|B)([0-9a-z]+)/g;
    let match;
    while ((match = regex.exec(hash)) !== null) {
      const role = HASH_CODE_TO_ROLE[match[1]];
      const items = this.#group(role)?.items ?? [];
      let item = null;
      if (VARIANT_HASH_ROLES.has(role)) {
        const variantId = parseInt(match[2], 36);
        item = items.find((i) => i.variantId === variantId) || null;
        // Free items inside variant-hashed roles (the free tails) serialize
        // as their 1-based index; pure-digit segments with no variant match
        // resolve that way. Real variant ids are far too large to collide.
        if (!item && /^\d+$/.test(match[2])) item = items[Number(match[2]) - 1] || null;
      } else {
        item = items[Number(match[2]) - 1] || null;
      }
      if (item) segments.push({ role, item });
    }
    return segments.length ? segments : null;
  }

  // Replace the whole configuration from a parsed hash. Roles missing from the
  // hash fall back to defaults: rope/toploop/tail take their first item, knots
  // are left out.
  #applyHashSegments(segments, hashMeta = {}) {
    this.#suppressHashSync = true;
    this.entries = [];
    this.stack = [];
    this.selectedUid = null;
    this.#uidCounter = 0;

    const firstItem = (role) => this.#group(role)?.items?.[0];
    const rope = segments.find((s) => s.role === 'rope')?.item ?? firstItem('rope');
    if (rope) this.entries.push({ uid: this.#nextUid(), role: 'rope', item: rope });
    if (!segments.some((s) => s.role === 'toploop')) {
      const item = firstItem('toploop');
      if (item) this.#addEntry('toploop', item);
    }
    // The tail is optional ("No Tail"): a hash without a TA segment means
    // the design deliberately has no tail, so nothing is auto-added.
    for (const segment of segments) {
      if (segment.role === 'rope') continue;
      this.#addEntry(segment.role, segment.item);
    }

    this.#applyHashMeta(hashMeta);

    this.render();
    this.#suppressHashSync = false;
    this.#syncHash();
  }

  // Scalar hash segments (not stack items): H<mm base36> = top-loop height,
  // L<ab> = letter tail letters. Invalid or absent values fall back to the
  // defaults so a shared hash always describes the full configuration.
  #parseHashMeta(hash) {
    const meta = {};
    const source = String(hash || '');
    const height = source.match(/H([0-9a-z]+)/);
    if (height) {
      const mm = parseInt(height[1], 36);
      if (Number.isFinite(mm)) meta.toploopHeightCm = mm / 10;
    }
    const letters = source.match(/L([a-z]{2})/);
    if (letters) meta.tailLetters = letters[1].toUpperCase();
    return meta;
  }

  #applyHashMeta(meta) {
    if (meta.toploopHeightCm == null) {
      this.toploopHeight = TOPLOOP_DEFAULT_HEIGHT_CM;
    } else {
      this.toploopHeight = Math.min(
        TOPLOOP_FULL_HEIGHT_CM,
        Math.max(TOPLOOP_MIN_HEIGHT_CM, meta.toploopHeightCm)
      );
    }
    const tail = this.#entriesByRole('tail')[0];
    this.tailLetters =
      tail && this.#isLetterTail(tail.item) && /^[A-Z]{2}$/.test(meta.tailLetters || '')
        ? meta.tailLetters
        : '';
  }

  #applyHashFromLocation() {
    const segments = this.#parseHash(window.location.hash);
    if (segments) this.#applyHashSegments(segments, this.#parseHashMeta(window.location.hash));
  }

  // Serialize the current configuration back into a hash string. Paid roles
  // encode their variantId in base36; free roles their 1-based item index.
  #serializeHash() {
    const parts = [];
    const rope = this.#entriesByRole('rope')[0];
    const indexOf = (role, item) => (this.#group(role)?.items ?? []).indexOf(item);
    if (rope) parts.push(`C${indexOf('rope', rope.item) + 1}`);
    for (const uid of this.stack) {
      const entry = this.#entry(uid);
      if (!entry) continue;
      const code = HASH_ROLE_TO_CODE[entry.role];
      if (!code) continue;
      if (VARIANT_HASH_ROLES.has(entry.role) && entry.item.variantId != null) {
        parts.push(`${code}${entry.item.variantId.toString(36)}`);
      } else {
        const index = indexOf(entry.role, entry.item);
        if (index >= 0) parts.push(`${code}${index + 1}`);
      }
    }
    const toploop = this.#entriesByRole('toploop')[0];
    if (toploop && Math.abs(this.toploopHeight - TOPLOOP_DEFAULT_HEIGHT_CM) > 1e-9) {
      parts.push(`H${Math.round(this.toploopHeight * 10).toString(36)}`);
    }
    const tail = this.#entriesByRole('tail')[0];
    if (tail && this.#isLetterTail(tail.item) && /^[A-Z]{2}$/.test(this.tailLetters)) {
      parts.push(`L${this.tailLetters.toLowerCase()}`);
    }
    return parts.join('');
  }

  // Keep the address bar hash in sync with the current configuration so the
  // page is always shareable. replaceState does not fire hashchange.
  #syncHash() {
    if (this.#suppressHashSync) return;
    const hash = this.#serializeHash();
    if (!hash || window.location.hash.slice(1) === hash) return;
    history.replaceState(null, '', `#${hash}`);
  }

  // Frame aspect ratio (width/height) of a sprite sheet, read from the sprite's
  // natural dimensions. Loads once and caches; returns null until loaded, then
  // re-renders the preview with the correct proportions.
  #spriteFrameAspect(item) {
    if (this.#spriteAspects.has(item.image)) return this.#spriteAspects.get(item.image);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const cols = Number(item.spriteColumns) || 10;
      const rows = Math.max(1, Math.ceil(Number(this.data.ropeCount) / cols));
      const aspect = img.naturalWidth / cols / (img.naturalHeight / rows);
      this.#spriteAspects.set(item.image, aspect);
      this.render();
    };
    img.src = item.image;
    return null;
  }

  // Aspect ratio (width/height) of a single (non-sprite) transparent image.
  // Loads once and caches; returns null until loaded, then re-renders.
  #imageAspect(item) {
    if (this.#imageAspects.has(item.image)) return this.#imageAspects.get(item.image);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const aspect = img.naturalWidth / img.naturalHeight;
      this.#imageAspects.set(item.image, aspect);
      this.render();
    };
    img.src = item.image;
    return null;
  }

  // Fallback aspect ratio (w/h) for sprite thumbnails before the image has
  // loaded. Matches the authored frame proportions:
  //   - Cobra knot / letters / metal / basic tails: 100:60
  //   - Ball style tails: 100:80
  //   - Top loops: 10:25
  #defaultSpriteAspect(item) {
    const title = item.title?.toLowerCase() || '';
    if (title.includes('loop')) return TOPLOOP_ART_FRAME_W_PX / TOPLOOP_ART_FRAME_H_PX;
    if (title.includes('ball')) return 100 / 80;
    if (title.includes('cobra') || title.includes('letter') || title.includes('metal') || title.includes('basic')) {
      return 100 / 60;
    }
    return 1;
  }

  // Use the current rope texture directly as a repeating background behind the
  // stack. The background is positioned so that the desired strip is visible
  // and tiled vertically.
  #ropeBackground() {
    const rope = this.#entriesByRole('rope')[0];
    if (!rope || !rope.item.image) return null;
    return rope.item.image;
  }

  // CSS background for the frame of a sprite sheet that matches the currently
  // selected rope colour (frame index = filename numeric suffix - 1). An
  // image/columns override addresses a different sheet than item.image (the
  // metal rings' rope extension).
  #spriteFrameStyle(item, image, colsOverride) {
    const cols = colsOverride ?? (Number(item.spriteColumns) || 1);
    const frames = Number(this.data.ropeCount) || cols;
    const rows = Math.max(1, Math.ceil(frames / cols));
    const idx = this.#currentRopeFrame();
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = cols === 1 ? 0 : (col / (cols - 1)) * 100;
    const y = rows === 1 ? 0 : (row / (rows - 1)) * 100;
    return {
      backgroundImage: `url("${image || item.image}")`,
      backgroundSize: `${cols * 100}% ${rows * 100}%`,
      backgroundPosition: `${x}% ${y}%`,
      backgroundRepeat: 'no-repeat',
    };
  }

  // The rope-loop thumb's window grows so its displayed height matches the
  // metal-ring thumbs (ring + braid tail), keeping all three modal options
  // the same height.
  #toploopThumbWindowPx() {
    const group = this.groups.find((g) => g.role === 'toploop');
    const metal = group?.items.find((i) => i.extensionImage);
    if (!metal) return TOPLOOP_THUMB_WINDOW_PX;
    const ringW = Number(metal.widthCm) || 0.53;
    const braidW = this.#toploopBraidWidthCm();
    const totalH = ringW / 2 + braidW * (TOPLOOP_THUMB_WINDOW_PX / TOPLOOP_ART_FRAME_W_PX);
    return TOPLOOP_ART_FRAME_W_PX * (totalH / ringW);
  }

  // Toploop sprite thumbs zoom the braid frame so the crop window fills the
  // thumb; the generic style would shrink the 30:844 strip to ~4px wide.
  #toploopThumbStyle(item) {
    const style = this.#spriteFrameStyle(item);
    const cols = Number(item.spriteColumns) || 1;
    style.backgroundSize = `${cols * 100}% ${(TOPLOOP_ART_FRAME_H_PX / this.#toploopThumbWindowPx()) * 100}%`;
    return style;
  }

  #toploopThumbAspect() {
    return TOPLOOP_ART_FRAME_W_PX / this.#toploopThumbWindowPx();
  }

  // Metal-ring toploop thumb: the ring on top with the current rope colour's
  // braid passing in front of its lower half. The braid tail keeps the same
  // 1:5 window as the rope-loop thumb so both options read as one slim strip.
  #renderToploopMetalThumb(item, className) {
    const ringW = Number(item.widthCm) || 0.53;
    const braidW = this.#toploopBraidWidthCm();
    const totalH = ringW / 2 + braidW * (TOPLOOP_THUMB_WINDOW_PX / TOPLOOP_ART_FRAME_W_PX);

    const wrap = document.createElement('span');
    wrap.className = className;
    const aspect = ringW / totalH;
    wrap.style.setProperty('--choice-thumb-aspect', String(aspect));
    wrap.style.setProperty('--content-aspect', String(aspect));

    const ring = document.createElement('img');
    ring.src = item.image;
    ring.alt = item.title;
    ring.loading = 'lazy';
    ring.style.position = 'relative';
    ring.style.left = `${TOPLOOP_RING_OFFSET_PCT * 100}%`;
    wrap.appendChild(ring);

    const braid = document.createElement('span');
    braid.className = 'lanyard-builder__metal-thumb-braid';
    Object.assign(
      braid.style,
      this.#spriteFrameStyle(item, item.extensionImage, Number(item.extensionColumns) || 1)
    );
    // One braid frame fills the braid box's width; the sheet height scales so
    // the frame's natural cm proportions hold inside the thumb.
    braid.style.width = `${(braidW / ringW) * 100}%`;
    braid.style.top = `${((ringW / 2) / totalH) * 100}%`;
    braid.style.height = `${100 - ((ringW / 2) / totalH) * 100}%`;
    const sheetHeightPct =
      (TOPLOOP_ART_FRAME_H_PX / TOPLOOP_ART_FRAME_W_PX) * (braidW / (totalH - ringW / 2)) * 100;
    braid.style.backgroundSize = `${(Number(item.extensionColumns) || 1) * 100}% ${sheetHeightPct}%`;
    wrap.appendChild(braid);
    return wrap;
  }

  // Braid display width for metal-ring extensions = the rope-loop style's
  // own braid width, so both styles share one source of truth.
  #toploopBraidWidthCm() {
    const group = this.groups.find((g) => g.role === 'toploop');
    const ropeLoop = group?.items.find((i) => this.#isSprite(i));
    return ropeLoop?.widthCm || TOPLOOP_DEFAULT_WIDTH_CM;
  }

  // -------------------------------------------------------------- rendering

  render() {
    this.#normalizeStackOrder();
    this.#normalizeToploopHeight();
    this.renderOptions();
    this.renderPreview();
    this.renderStackPanel();
    this.updateTotal();
    if (this.openGroupRole) this.renderModal();
    this.#saveState();
    this.#syncHash();
  }

  renderOptions() {
    const wrap = this.refs.options;
    const activeGroups = this.groups.filter((group) => group.items && group.items.length > 0);

    // Rebuild the row scaffolding only when the group set changes. Rows are
    // updated in place afterwards, so selecting/dragging does not re-create
    // the thumbnails (product <img>/sprite thumbs flash while re-decoding).
    const needsRebuild =
      wrap.children.length !== activeGroups.length ||
      activeGroups.some((group, i) => wrap.children[i].dataset.role !== group.role);

    if (needsRebuild) {
      wrap.innerHTML = '';
      for (const group of activeGroups) {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'lanyard-builder__group-row';
        row.dataset.role = group.role;

        const label = document.createElement('span');
        label.className = 'lanyard-builder__group-row__label';
        label.textContent = group.label;
        row.appendChild(label);

        const summary = document.createElement('span');
        summary.className = 'lanyard-builder__group-row__summary';
        row.appendChild(summary);

        const action = document.createElement('span');
        action.className = 'lanyard-builder__group-row__chevron';
        if (group.quickAdd) {
          action.classList.add('lanyard-builder__group-row__chevron--plus');
          action.textContent = '+';
          row.addEventListener('click', () => this.#quickAdd(group));
        } else {
          action.textContent = '›';
          row.addEventListener('click', () => this.#openGroup(group.role));
        }
        row.appendChild(action);

        wrap.appendChild(row);
      }
    }

    for (const [index, group] of activeGroups.entries()) {
      const row = wrap.children[index];
      const summary = row.querySelector('.lanyard-builder__group-row__summary');
      if (summary) summary.textContent = this.#groupSummary(group);

      const entries = this.#entriesByRole(group.role);
      const last = entries.length ? entries[entries.length - 1].item : null;
      // Sprite thumbs (top loop / knot / tail) are drawn from the frame that
      // matches the current rope colour, so the rope frame is part of their
      // identity — without it they keep showing the previous colour. Metal
      // ring toploop thumbs compose a braid frame too, so they follow as well.
      const frame =
        last && (this.#isSprite(last) || last.extensionImage) ? this.#currentRopeFrame() : '';
      const thumbKey = `${entries.length}|${last?.title ?? ''}|${frame}`;
      if (row.dataset.thumbKey === thumbKey) continue;
      row.dataset.thumbKey = thumbKey;

      const oldThumb = row.querySelector('.lanyard-builder__group-row__thumb-frame');
      const newThumb = this.#renderRowThumb(last, group.role, entries.length);
      if (newThumb) {
        if (oldThumb) {
          oldThumb.replaceWith(newThumb);
        } else {
          row.insertBefore(newThumb, row.querySelector('.lanyard-builder__group-row__chevron'));
        }
      } else {
        oldThumb?.remove();
      }
    }
  }

  #groupSummary(group) {
    const entries = this.#entriesByRole(group.role);
    if (entries.length === 0) return group.role === 'tail' ? 'None' : 'Select';
    if (group.mode === 'single') return entries[0].item.title;
    return `${entries.length} selected`;
  }

  #lastSelectedItem(group) {
    const entries = this.#entriesByRole(group.role);
    return entries.length ? entries[entries.length - 1].item : null;
  }

  #renderRowThumb(item, role, count = 0) {
    if (!item || !item.image) return null;

    const frame = document.createElement('span');
    frame.className = 'lanyard-builder__group-row__thumb-frame';

    if (count > 0 && (role === 'knot' || role === 'beads' || role === 'decoration')) {
      const badge = document.createElement('span');
      badge.className = 'lanyard-builder__group-row__count';
      badge.textContent = `x${count}`;
      frame.appendChild(badge);
    }

    if (role === 'toploop' && item.extensionImage) {
      const content = this.#renderToploopMetalThumb(
        item,
        'lanyard-builder__group-row__thumb-content lanyard-builder__group-row__thumb-content--metal'
      );
      frame.appendChild(content);
      return frame;
    }

    if (this.#isSprite(item)) {
      const content = document.createElement('span');
      content.className = 'lanyard-builder__group-row__thumb-content';
      const isToploop = role === 'toploop';
      Object.assign(content.style, isToploop ? this.#toploopThumbStyle(item) : this.#spriteFrameStyle(item));

      // Let CSS fit the sprite frame inside the thumb frame while keeping its
      // natural aspect ratio, so it still looks correct when the mobile frame
      // shrinks to 32px. Toploop thumbs use the cropped window aspect instead
      // of the full 30:844 strip.
      const aspect = isToploop
        ? this.#toploopThumbAspect()
        : this.#spriteFrameAspect(item) ?? this.#defaultSpriteAspect(item);
      content.style.setProperty('--content-aspect', String(aspect));
      frame.appendChild(content);
      return frame;
    }

    if (role === 'rope') {
      const content = document.createElement('span');
      content.className = 'lanyard-builder__group-row__thumb-content lanyard-builder__group-row__thumb-content--color';
      content.style.backgroundImage = `url("${item.image}")`;
      content.style.setProperty('--content-aspect', '1');
      content.setAttribute('role', 'img');
      content.setAttribute('aria-label', item.title);
      frame.appendChild(content);
      return frame;
    }

    const img = document.createElement('img');
    img.className = 'lanyard-builder__group-row__thumb';
    img.src = item.image;
    img.alt = item.title;
    frame.appendChild(img);
    return frame;
  }

  #quickAdd(group) {
    const item = group.items[0];
    if (!item) return;
    this.#addEntry(group.role, item);
    this.render();
  }

  #renderChoiceCard(group, item) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lanyard-builder__choice-card';
    btn.dataset.role = group.role;
    btn.dataset.variantId = String(item.variantId);

    const count = this.#entriesByRole(group.role).filter((e) => this.#itemKey(e.item) === this.#itemKey(item)).length;
    btn.dataset.selected = String(count > 0);

    // Stock: -1 means unknown/untracked (no limit shown). Remaining accounts
    // for how many of this item are already in the design.
    const stock = item.inventory == null ? -1 : Number(item.inventory);
    const remaining = stock < 0 ? Infinity : stock - count;
    const soldOut = remaining <= 0;
    if (soldOut) {
      btn.dataset.soldOut = 'true';
      btn.setAttribute('aria-disabled', 'true');
      const overlay = document.createElement('span');
      overlay.className = 'lanyard-builder__choice-overlay';
      btn.appendChild(overlay);
      const flag = document.createElement('span');
      flag.className = 'lanyard-builder__choice-flag lanyard-builder__choice-flag--soldout';
      flag.textContent = 'SOLD OUT';
      btn.appendChild(flag);
    } else if (stock >= 0 && stock <= 2) {
      const flag = document.createElement('span');
      flag.className = 'lanyard-builder__choice-flag lanyard-builder__choice-flag--lowstock';
      flag.textContent = `Only ${stock} in stock`;
      btn.appendChild(flag);
    }

    const thumbImage = item.modalImage || item.image;
    if (thumbImage) {
      if (this.#isSprite(item)) {
        const thumb = document.createElement('div');
        thumb.className = 'lanyard-builder__choice-thumb lanyard-builder__choice-thumb--sprite';
        const isToploop = group.role === 'toploop';
        const aspect = isToploop
          ? this.#toploopThumbAspect()
          : this.#spriteFrameAspect(item) ?? this.#defaultSpriteAspect(item);
        thumb.style.setProperty('--choice-thumb-aspect', String(aspect));
        Object.assign(thumb.style, isToploop ? this.#toploopThumbStyle(item) : this.#spriteFrameStyle(item));
        btn.appendChild(thumb);
      } else if (group.role === 'toploop' && item.extensionImage) {
        btn.appendChild(
          this.#renderToploopMetalThumb(item, 'lanyard-builder__choice-thumb lanyard-builder__choice-thumb--metal')
        );
      } else {
        const img = document.createElement('img');
        img.className = 'lanyard-builder__choice-thumb';
        img.src = thumbImage;
        img.alt = item.title;
        img.loading = 'lazy';
        // Spacer canvases are 3:3 squares with the object in a narrow
        // horizontal band: a wrapper crops the empty top/bottom so the card
        // stays compact while the image renders wider than the column.
        if (group.role === 'decoration') {
          const crop = document.createElement('div');
          crop.className = 'lanyard-builder__choice-thumb-wrap';
          crop.appendChild(img);
          btn.appendChild(crop);
        } else {
          btn.appendChild(img);
        }
      }
    }

    const label = document.createElement('span');
    label.className = 'lanyard-builder__choice-label';
    label.textContent = item.title;
    btn.appendChild(label);

    // Every card reserves a price line so rows stay aligned; free options
    // (Ball Style / Basic Style tails) read "Free" instead of no tag.
    const price = document.createElement('span');
    price.className = 'lanyard-builder__choice-price';
    price.textContent = item.priceCents > 0 ? this.#format(item.priceCents) : 'Free';
    btn.appendChild(price);

    if (count > 0 && group.role === 'tail' && this.#isLetterTail(item)) {
      const badges = document.createElement('span');
      badges.className = 'lanyard-builder__choice-badges';

      const letters = document.createElement('span');
      letters.className = 'lanyard-builder__choice-count lanyard-builder__choice-count--letters';
      letters.setAttribute('role', 'button');
      letters.setAttribute('aria-label', 'Change letters');
      letters.textContent = /^[A-Z]{2}$/.test(this.tailLetters) ? this.tailLetters : '✓';
      letters.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#openLetterPicker(item);
      });
      badges.appendChild(letters);

      const change = document.createElement('span');
      change.className = 'lanyard-builder__choice-change';
      change.setAttribute('role', 'button');
      change.setAttribute('aria-label', 'Change letters');
      change.textContent = '↻';
      change.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#openLetterPicker(item);
      });
      badges.appendChild(change);

      btn.appendChild(badges);
    } else if (count > 0) {
      const badge = document.createElement('span');
      badge.className = 'lanyard-builder__choice-count';
      badge.textContent = count > 1 ? `×${count}` : '✓';
      btn.appendChild(badge);
    }

    btn.addEventListener('click', () => this.#onChoiceClick(group, item));
    return btn;
  }

  // ------------------------------------------------------------- modal

  #ensureModal() {
    if (this.modal) return this.modal;

    const modal = document.createElement('div');
    modal.className = 'lanyard-builder__modal';
    modal.hidden = true;

    const backdrop = document.createElement('div');
    backdrop.className = 'lanyard-builder__modal-backdrop';
    backdrop.addEventListener('click', () => this.#closeModal());
    modal.appendChild(backdrop);

    const panel = document.createElement('div');
    panel.className = 'lanyard-builder__modal-panel';

    const header = document.createElement('div');
    header.className = 'lanyard-builder__modal-header';
    const title = document.createElement('h3');
    title.className = 'lanyard-builder__modal-title';
    header.appendChild(title);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'lanyard-builder__modal-close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.addEventListener('click', () => this.#closeModal());
    header.appendChild(close);
    panel.appendChild(header);

    const tailSlot = document.createElement('div');
    tailSlot.className = 'lanyard-builder__modal-tail';
    panel.appendChild(tailSlot);

    const body = document.createElement('div');
    body.className = 'lanyard-builder__modal-body';

    const left = document.createElement('button');
    left.type = 'button';
    left.className = 'lanyard-builder__modal-arrow';
    left.setAttribute('aria-label', 'Scroll left');
    left.textContent = '‹';
    left.addEventListener('click', () => this.#scrollModal(-1));
    body.appendChild(left);

    const scroll = document.createElement('div');
    scroll.className = 'lanyard-builder__modal-scroll';
    body.appendChild(scroll);

    const right = document.createElement('button');
    right.type = 'button';
    right.className = 'lanyard-builder__modal-arrow';
    right.setAttribute('aria-label', 'Scroll right');
    right.textContent = '›';
    right.addEventListener('click', () => this.#scrollModal(1));
    body.appendChild(right);

    panel.appendChild(body);
    modal.appendChild(panel);

    this.appendChild(modal);
    this.modal = modal;
    return modal;
  }

  #openGroup(role, swapUid = null) {
    this.openGroupRole = role;
    this.swapTargetUid = swapUid;
    this.#resetModalScroll = true;
    const modal = this.#ensureModal();
    modal.hidden = false;
    document.body.classList.add('lanyard-builder--modal-open');
    this.renderModal();
  }

  #closeModal() {
    this.openGroupRole = null;
    this.swapTargetUid = null;
    if (this.modal) this.modal.hidden = true;
    document.body.classList.remove('lanyard-builder--modal-open');
  }

  #renderModalSection(group, title, items, note) {
    const section = document.createElement('div');
    section.className = 'lanyard-builder__modal-section';

    const heading = document.createElement('div');
    heading.className = 'lanyard-builder__modal-section-title';
    heading.textContent = title;
    section.appendChild(heading);

    if (note) {
      const caption = document.createElement('p');
      caption.className = 'lanyard-builder__modal-section-note';
      caption.textContent = note;
      section.appendChild(caption);
    }

    const row = document.createElement('div');
    row.className = 'lanyard-builder__modal-row';
    for (const item of items) {
      row.appendChild(this.#renderChoiceCard(group, item));
    }
    section.appendChild(row);
    return section;
  }

  #scrollModal(direction) {
    if (!this.modal) return;
    // Sectioned modals keep one horizontal scroller per section; scroll them
    // together so the arrows still work.
    const targets = this.modal.querySelectorAll('.lanyard-builder__modal-scroll, .lanyard-builder__modal-row');
    for (const target of targets) {
      target.scrollBy({ left: direction * target.clientWidth * 0.8, behavior: 'smooth' });
    }
  }

  renderModal() {
    if (!this.openGroupRole || !this.modal) return;
    const group = this.#group(this.openGroupRole);
    if (!group) return;

    this.modal.querySelector('.lanyard-builder__modal-title').textContent = MODAL_TITLES[group.role] || group.label;

    const scroll = this.modal.querySelector('.lanyard-builder__modal-scroll');
    const prevScrollLeft = scroll.scrollLeft;
    const resetScroll = this.#resetModalScroll;
    this.#resetModalScroll = false;
    scroll.innerHTML = '';
    if (group.role === 'tail') {
      // The tail modal is split into labelled sections: standard tails first,
      // tail beads (top-hole-only beads) second.
      this.modal.dataset.layout = 'sections';
      scroll.classList.add('lanyard-builder__modal-scroll--sections');
      const standard = group.items.filter((item) => !item.isTailBead);
      const tailBeads = group.items.filter((item) => item.isTailBead);
      scroll.appendChild(this.#renderModalSection(group, 'Standard Tails', standard, null));
      if (tailBeads.length) {
        scroll.appendChild(
          this.#renderModalSection(group, 'Tail Beads', tailBeads, 'These beads can only sit at the bottom of the lanyard.')
        );
      }
    } else if (GRID_MODAL_ROLES.has(group.role)) {
      // Beads / rope colors / spacers: a tall 3-column grid that scrolls
      // vertically, so large catalogs stay browsable.
      this.modal.dataset.layout = 'grid';
      scroll.classList.remove('lanyard-builder__modal-scroll--sections');
      for (const item of group.items) {
        scroll.appendChild(this.#renderChoiceCard(group, item));
      }
    } else {
      this.modal.dataset.layout = 'row';
      scroll.classList.remove('lanyard-builder__modal-scroll--sections');
      for (const item of group.items) {
        scroll.appendChild(this.#renderChoiceCard(group, item));
      }
    }
    // Opening a different group starts at the top (the scroll container is
    // shared, and scroll anchoring can otherwise resurrect the old offset);
    // in-place re-renders keep their horizontal position.
    if (resetScroll) {
      scroll.scrollTop = 0;
      scroll.scrollLeft = 0;
    } else {
      scroll.scrollLeft = prevScrollLeft;
    }

    const tailSlot = this.modal.querySelector('.lanyard-builder__modal-tail');
    tailSlot.innerHTML = '';
    if (group.role === 'tail') {
      tailSlot.appendChild(this.#renderTailInput());
    } else if (group.role === 'toploop') {
      tailSlot.appendChild(this.#renderToploopControl());
    }
  }

  #renderTailInput() {
    const wrap = document.createElement('div');
    wrap.className = 'lanyard-builder__tail-input';
    wrap.hidden = true;

    const tail = this.#entriesByRole('tail')[0];
    const style = tail?.item?.tailStyle;

    // Letter tails pick their letters in the dedicated letter modal instead.
    if (tail && style === 'metal') {
      wrap.hidden = false;

      const label = document.createElement('label');
      label.className = 'lanyard-builder__tail-label';
      label.textContent = 'Finish / color';
      wrap.appendChild(label);

      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'field__input';
      input.value = this.tailInput;
      input.placeholder = 'e.g. brushed silver';
      input.addEventListener('input', () => {
        this.tailInput = input.value;
      });
      wrap.appendChild(input);
    }

    return wrap;
  }

  // Metal-ring styles render the ring inside the loop height, so the reveal
  // cannot go below the ring's own height (rounded up to the slider step).
  #toploopMinHeightCm(item) {
    if (!item?.extensionImage) return TOPLOOP_MIN_HEIGHT_CM;
    return Math.ceil((Number(item.widthCm) || 0.53) * 10) / 10;
  }

  #normalizeToploopHeight() {
    const item = this.#entriesByRole('toploop')[0]?.item;
    const min = this.#toploopMinHeightCm(item);
    if (this.toploopHeight < min) this.toploopHeight = min;
  }

  #renderToploopControl() {
    const wrap = document.createElement('div');
    wrap.className = 'lanyard-builder__toploop-control';

    const label = document.createElement('label');
    label.className = 'lanyard-builder__tail-label';
    label.textContent = 'Loop height';
    wrap.appendChild(label);

    const input = document.createElement('input');
    input.type = 'range';
    input.className = 'lanyard-builder__toploop-range';
    input.min = String(this.#toploopMinHeightCm(this.#entriesByRole('toploop')[0]?.item));
    input.max = String(TOPLOOP_FULL_HEIGHT_CM);
    input.step = '0.1';
    input.value = String(this.toploopHeight);
    input.setAttribute('aria-label', 'Top loop height');
    wrap.appendChild(input);

    const value = document.createElement('span');
    value.className = 'lanyard-builder__toploop-value';
    value.textContent = `${this.toploopHeight.toFixed(1)} cm`;
    wrap.appendChild(value);

    input.addEventListener('input', () => {
      this.toploopHeight = Number(input.value);
      value.textContent = `${this.toploopHeight.toFixed(1)} cm`;
      this.renderPreview();
      this.#saveState();
      this.#syncHash();
    });

    return wrap;
  }

  // ---------------------------------------------------------- letter picker
  // A letter-style tail requires exactly two letters, chosen in a dedicated
  // stacked modal. Cancel keeps the previously selected tail untouched.

  #isLetterTail(item) {
    if (!item) return false;
    const style = String(item.tailStyle || '').toLowerCase().trim();
    if (style === 'letter') return true;
    return String(item.title || '').toLowerCase().includes('letter');
  }

  #ensureLetterModal() {
    if (this.letterModal) return this.letterModal;

    const modal = document.createElement('div');
    modal.className = 'lanyard-builder__modal lanyard-builder__letter-modal';
    modal.hidden = true;

    const backdrop = document.createElement('div');
    backdrop.className = 'lanyard-builder__modal-backdrop';
    backdrop.addEventListener('click', () => this.#closeLetterPicker());
    modal.appendChild(backdrop);

    const panel = document.createElement('div');
    panel.className = 'lanyard-builder__modal-panel lanyard-builder__letter-panel';

    const header = document.createElement('div');
    header.className = 'lanyard-builder__modal-header';
    const title = document.createElement('h3');
    title.className = 'lanyard-builder__modal-title';
    title.textContent = 'Choose 2 letters';
    header.appendChild(title);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'lanyard-builder__modal-close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.addEventListener('click', () => this.#closeLetterPicker());
    header.appendChild(close);
    panel.appendChild(header);

    const body = document.createElement('div');
    body.className = 'lanyard-builder__letter-body';

    const slots = document.createElement('div');
    slots.className = 'lanyard-builder__letter-slots';
    for (let i = 0; i < 2; i++) {
      const slot = document.createElement('button');
      slot.type = 'button';
      slot.className = 'lanyard-builder__letter-slot';
      slot.dataset.slot = String(i);
      slot.addEventListener('click', () => {
        this.letterSlot = i;
        this.#renderLetterPickerState();
      });
      slots.appendChild(slot);
    }
    body.appendChild(slots);

    const grid = document.createElement('div');
    grid.className = 'lanyard-builder__letter-grid';
    for (const letter of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
      const key = document.createElement('button');
      key.type = 'button';
      key.className = 'lanyard-builder__letter-key';
      key.textContent = letter;
      key.addEventListener('click', () => {
        this.letterDraft[this.letterSlot] = letter;
        const nextEmpty = this.letterDraft.findIndex((l) => !l);
        this.letterSlot = nextEmpty === -1 ? (this.letterSlot + 1) % 2 : nextEmpty;
        this.#renderLetterPickerState();
      });
      grid.appendChild(key);
    }
    body.appendChild(grid);

    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'button lanyard-builder__letter-confirm';
    confirm.textContent = 'Confirm';
    confirm.addEventListener('click', () => this.#confirmLetterPick());
    body.appendChild(confirm);

    panel.appendChild(body);
    modal.appendChild(panel);
    this.appendChild(modal);
    this.letterModal = modal;
    return modal;
  }

  #openLetterPicker(item) {
    this.#letterPendingItem = item;
    const current = this.#entriesByRole('tail')[0];
    const sameItem = current && this.#itemKey(current.item) === this.#itemKey(item);
    this.letterDraft = sameItem && /^[A-Z]{2}$/.test(this.tailLetters) ? this.tailLetters.split('') : [null, null];
    const firstEmpty = this.letterDraft.findIndex((l) => !l);
    this.letterSlot = firstEmpty === -1 ? 0 : firstEmpty;
    this.#ensureLetterModal().hidden = false;
    this.#renderLetterPickerState();
  }

  #closeLetterPicker() {
    // Cancelled: the tail selection stays whatever it was before.
    this.#letterPendingItem = null;
    if (this.letterModal) this.letterModal.hidden = true;
  }

  #renderLetterPickerState() {
    if (!this.letterModal || this.letterModal.hidden) return;
    this.letterModal.querySelectorAll('.lanyard-builder__letter-slot').forEach((slot, i) => {
      slot.textContent = this.letterDraft[i] || '–';
      slot.dataset.active = String(i === this.letterSlot);
      slot.dataset.filled = String(Boolean(this.letterDraft[i]));
    });
    const confirm = this.letterModal.querySelector('.lanyard-builder__letter-confirm');
    if (confirm) confirm.disabled = this.letterDraft.some((l) => !l);
  }

  #confirmLetterPick() {
    if (this.letterDraft.some((l) => !l)) return;
    const item = this.#letterPendingItem;
    this.#closeLetterPicker();
    if (!item) return;

    this.tailLetters = this.letterDraft.join('');

    if (this.swapTargetUid != null) {
      const target = this.#entry(this.swapTargetUid);
      if (target && target.role === 'tail') target.item = item;
      this.swapTargetUid = null;
    } else {
      const existing = this.#entriesByRole('tail')[0];
      if (existing) {
        existing.item = item;
      } else {
        this.#addEntry('tail', item);
      }
    }

    // The choice modal stays open so the letters badge is visible immediately.
    this.render();
  }

  // ------------------------------------------------------------ interactions

  #onChoiceClick(group, item) {
    // Sold out — including stock exhausted by this design's own selections —
    // is not selectable.
    const stock = item.inventory == null ? -1 : Number(item.inventory);
    if (stock >= 0) {
      const count = this.#entriesByRole(group.role).filter((e) => this.#itemKey(e.item) === this.#itemKey(item)).length;
      if (count >= stock) return;
    }

    // Letter tails route through the letter picker: they are only selected
    // once two letters are confirmed there.
    if (group.role === 'tail' && this.#isLetterTail(item)) {
      this.#openLetterPicker(item);
      return;
    }

    if (this.swapTargetUid != null) {
      // Swapping an existing stack entry via the choice modal.
      const target = this.#entry(this.swapTargetUid);
      if (target && target.role === group.role) {
        if (this.#itemKey(target.item) !== this.#itemKey(item)) {
          target.item = item;
        }
      }
      this.swapTargetUid = null;
      this.#closeModal();
      this.render();
      return;
    }

    if (group.role === 'toploop' || group.role === 'tail') {
      // The top loop is required (the tail is optional): clicking the current
      // one is a no-op, clicking a different one swaps it in place.
      const existing = this.#entriesByRole(group.role)[0];
      if (existing && this.#itemKey(existing.item) === this.#itemKey(item)) {
        // no-op
      } else if (existing) {
        existing.item = item;
      } else {
        this.#addEntry(group.role, item);
      }
    } else if (group.mode === 'single') {
      const existing = this.#entriesByRole(group.role)[0];
      if (existing && this.#itemKey(existing.item) === this.#itemKey(item)) {
        this.#removeEntry(existing.uid);
      } else {
        if (existing) this.#removeEntry(existing.uid);
        this.#addEntry(group.role, item);
      }
    } else {
      this.#addEntry(group.role, item);
    }

    this.#closeModal();
    this.render();
  }

  #addEntry(role, item) {
    const uid = this.#nextUid();
    this.entries.push({ uid, role, item });
    if (!this.#isStackable(role)) return;

    if (role === 'toploop') {
      // Top loops always sit at the top of the stack; nothing may go above.
      this.stack.unshift(uid);
      return;
    }

    if (role === 'tail') {
      // Tails always sit at the bottom of the stack.
      this.stack.push(uid);
      return;
    }

    // When an item is selected in the preview, insert the new one just below
    // it. Otherwise fall back to placing it above the tail block (or at the end).
    if (this.selectedUid != null && this.stack.includes(this.selectedUid)) {
      const index = this.stack.indexOf(this.selectedUid);
      this.stack.splice(index + 1, 0, uid);
      return;
    }

    // Insert non-tail items just above the tail block (or at the end if no tail).
    const firstTailIndex = this.stack.findIndex((id) => this.#entry(id)?.role === 'tail');
    if (firstTailIndex >= 0) {
      this.stack.splice(firstTailIndex, 0, uid);
    } else {
      this.stack.push(uid);
    }
  }

  #removeEntry(uid) {
    const entry = this.#entry(uid);
    if (entry && entry.role === 'toploop') return;
    this.entries = this.entries.filter((e) => e.uid !== uid);
    this.stack = this.stack.filter((id) => id !== uid);
    if (this.selectedUid === uid) this.selectedUid = null;
  }

  #selectEntry(uid) {
    this.selectedUid = this.selectedUid === uid ? null : uid;
    this.render();
  }

  // Enforce the invariant: every top loop sits above every other item, and
  // every tail sits below every non-tail item.
  #normalizeStackOrder() {
    const rank = (uid) => {
      const role = this.#entry(uid)?.role;
      if (role === 'toploop') return 0;
      if (role === 'tail') return 2;
      return 1;
    };
    this.stack.sort((a, b) => rank(a) - rank(b));
  }

  // ---------------------------------------------------------------- preview

  // Total rendered height of the stack at a given scale, accounting for
  // intentional overlaps between components.
  #stackTotalHeight(scale = this.scale) {
    let totalPx = 0;
    for (const uid of this.stack) {
      const entry = this.#entry(uid);
      const overlap = entry ? this.#overlapPx(entry.role) : 0;
      totalPx += this.#entryHeightCm(entry) * scale - overlap;
    }
    return totalPx;
  }

  // Scale down the whole stack proportionally when it would overflow the stage.
  #effectiveScale() {
    const stageH = (this.refs.stage.clientHeight || 500) - 16;
    const totalPx = this.#stackTotalHeight();
    if (totalPx <= stageH) return this.scale;
    return (stageH / totalPx) * this.scale;
  }

  renderPreview() {
    const stage = this.refs.stage;
    stage.innerHTML = '';

    const s = this.#effectiveScale();

    const layers = [];
    for (const uid of this.stack) {
      const entry = this.#entry(uid);
      if (!entry) continue;
      layers.push({ role: entry.role, item: entry.item, uid, image: entry.item.image, heightCm: entry.item.heightCm || 1 });
    }

    const stageH = stage.clientHeight || 500;
    const totalH = this.#stackTotalHeight(s);
    let cursor = Math.max(0, (stageH - totalH) / 2);

    // Rope background strip sits behind every component except the top loop and
    // the tail — neither has rope running through it.
    const ropeBg = this.#ropeBackground();
    if (ropeBg) {
      let ropeTop = cursor;
      let ropeHeight = 0;
      for (const uid of this.stack) {
        const entry = this.#entry(uid);
        if (!entry) continue;
        const overlap = entry ? this.#overlapPx(entry.role) : 0;
        if (entry.role === 'toploop') {
          ropeTop += this.#entryHeightCm(entry) * s - overlap;
          continue;
        }
        if (entry.role === 'tail') break;
        ropeHeight += this.#entryHeightCm(entry) * s - overlap;
      }

      const ropeW = 12;
      const tileH = (888 * ropeW) / 33;
      const ropeEl = document.createElement('div');
      ropeEl.className = 'lanyard-builder__rope';
      ropeEl.dataset.role = 'rope';
      ropeEl.style.top = `${ropeTop}px`;
      ropeEl.style.left = '50%';
      ropeEl.style.width = `${ropeW}px`;
      ropeEl.style.height = `${ropeHeight}px`;
      ropeEl.style.backgroundImage = `url("${ropeBg}")`;
      ropeEl.style.backgroundRepeat = 'repeat-y';
      // Keep the source image at its natural resolution; position it so the
      // requested strip is visible, shifted left by 20px from the given coord.
      ropeEl.style.backgroundSize = '512px 512px';
      ropeEl.style.backgroundPosition = '-238px -34px';
      stage.appendChild(ropeEl);
    }

    for (const layer of layers) {
      const stackIndex = this.stack.indexOf(layer.uid);
      const entry = this.#entry(layer.uid);
      const occupied = this.#entryHeightCm(entry) * s;
      let visualH = occupied;
      let visualW = (layer.item.widthCm || OBJECT_WIDTH_CM) * s;

      // Fixed real-world widths for specific component shapes. Knots and ball
      // tails keep their own aspect ratio (100:60 and 100:80), so their visual
      // height is derived from the fixed width, not from preview_height. Top
      // loops use a fixed width and a user-controlled reveal height.
      if (layer.role === 'knot') {
        visualW = KNOT_WIDTH_CM * s;
        visualH = visualW * 0.6;
      } else if (layer.role === 'toploop') {
        visualW = (layer.item.widthCm || TOPLOOP_DEFAULT_WIDTH_CM) * s;
        visualH = occupied;
      } else if (layer.role === 'tail' && layer.item.title?.toLowerCase().includes('ball')) {
        visualW = BALL_WIDTH_CM * s;
        visualH = visualW * 0.8;
      } else if (this.#isSprite(layer.item)) {
        const aspect = this.#spriteFrameAspect(layer.item);
        if (aspect) visualW = occupied * aspect;
      }
      // Non-sprite items keep the configured width (default 3cm) and a
      // container height equal to preview_height. The img inside keeps its
      // original aspect ratio and is centred vertically; the overflow is
      // clipped by the layer.

      const wrapper = document.createElement('div');
      wrapper.className = 'lanyard-builder__layer-wrapper';
      const tailTitle = layer.item.title?.toLowerCase() || '';
      const tailStyle = String(layer.item.tailStyle || '').toLowerCase().trim();
      if (layer.role === 'tail' && (
        tailStyle === 'letter' || tailStyle === 'metal' ||
        tailTitle.includes('letter') || tailTitle.includes('metal tail')
      )) {
        wrapper.classList.add('lanyard-builder__layer-wrapper--tail-offset');
      }
      wrapper.dataset.role = layer.role;
      wrapper.dataset.uid = String(layer.uid);
      wrapper.style.top = `${cursor}px`;
      wrapper.style.left = '50%';
      wrapper.style.width = `${visualW}px`;
      wrapper.style.height = `${visualH}px`;
      wrapper.style.zIndex = String((ROLE_Z[layer.role] ?? 1) * 1000 + stackIndex);

      if (this.selectedUid === layer.uid) {
        wrapper.classList.add('lanyard-builder__layer-wrapper--selected');
      }

      const content = document.createElement('div');
      content.className = 'lanyard-builder__layer';
      content.dataset.role = layer.role;

      if (layer.image) {
        if (this.#isSprite(layer.item)) {
          if (layer.role === 'toploop') {
            // Draw the full loop at full scale inside the (smaller) layer and
            // anchor it to the top, so only the revealed portion is visible.
            content.style.alignItems = 'flex-start';
            const inner = document.createElement('div');
            inner.className = 'lanyard-builder__layer-inner';
            inner.style.width = '100%';
            inner.style.height = `${TOPLOOP_FULL_HEIGHT_CM * s}px`;
            Object.assign(inner.style, this.#spriteFrameStyle(layer.item));
            content.appendChild(inner);
          } else {
            Object.assign(content.style, this.#spriteFrameStyle(layer.item));
          }
        } else if (layer.role === 'toploop' && layer.item.extensionImage) {
          // Metal ring: a small ring sits at the top and the braid passes IN
          // FRONT of its lower half (the wrap), starting at the ring's
          // vertical middle — the braid element is appended after the ring so
          // it paints above it.
          content.style.alignItems = 'flex-start';
          content.style.position = 'relative';
          const ringH = (Number(layer.item.widthCm) || 0.53) * s;

          const ring = document.createElement('img');
          ring.crossOrigin = 'anonymous';
          ring.draggable = false;
          ring.src = layer.image;
          ring.alt = layer.item.title;
          ring.style.position = 'absolute';
          ring.style.top = '0';
          ring.style.left = '50%';
          ring.style.transform = `translateX(${-50 + TOPLOOP_RING_OFFSET_PCT * 100}%)`;
          ring.style.width = '100%';
          ring.style.height = 'auto';
          content.appendChild(ring);

          const inner = document.createElement('div');
          inner.className = 'lanyard-builder__layer-inner';
          inner.style.position = 'absolute';
          inner.style.top = `${ringH / 2}px`;
          inner.style.left = '0';
          inner.style.right = '0';
          inner.style.marginInline = 'auto';
          inner.style.width = `${this.#toploopBraidWidthCm() * s}px`;
          inner.style.height = `${TOPLOOP_FULL_HEIGHT_CM * s}px`;
          Object.assign(
            inner.style,
            this.#spriteFrameStyle(
              layer.item,
              layer.item.extensionImage,
              Number(layer.item.extensionColumns) || 1
            )
          );
          content.appendChild(inner);
        } else {
          const img = document.createElement('img');
          img.crossOrigin = 'anonymous';
          img.draggable = false;
          img.src = layer.image;
          img.alt = layer.item.title;
          const aspect = this.#imageAspect(layer.item);
          if (aspect) {
            img.style.width = '100%';
            img.style.height = `${visualW / aspect}px`;
          }
          content.appendChild(img);
        }
      }

      wrapper.appendChild(content);

      if (this.selectedUid === layer.uid) {
        const actions = document.createElement('div');
        actions.className = 'lanyard-builder__layer-actions';

        const canChange = layer.role !== 'knot';
        const canDelete = layer.role !== 'toploop';

        if (canChange) {
          const changeBtn = document.createElement('button');
          changeBtn.type = 'button';
          changeBtn.className = 'lanyard-builder__layer-action';
          changeBtn.setAttribute('aria-label', `Change ${layer.role}`);
          changeBtn.textContent = '⇄';
          changeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.#openGroup(layer.role, layer.uid);
          });
          actions.appendChild(changeBtn);
        }

        if (canDelete) {
          const deleteBtn = document.createElement('button');
          deleteBtn.type = 'button';
          deleteBtn.className = 'lanyard-builder__layer-action';
          deleteBtn.setAttribute('aria-label', 'Remove');
          deleteBtn.textContent = '×';
          deleteBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.#removeEntry(layer.uid);
            this.render();
          });
          actions.appendChild(deleteBtn);
        }

        wrapper.appendChild(actions);
      }

      wrapper.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button')) return;
        this.#startDrag(layer.uid, event, stage);
      });

      stage.appendChild(wrapper);

      const overlap = this.#overlapPx(layer.role);
      cursor += occupied - overlap;
    }
  }

  // ------------------------------------------------------------- drag & drop

  #startDrag(uid, event, container, { immediate = false } = {}) {
    event.preventDefault();
    const el = event.target.closest('[data-uid]');
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const clientY = event.clientY ?? event.touches?.[0]?.clientY;
    if (clientY == null) return;

    const isStack = container !== this.refs.stage;
    let slotH = 0;
    if (isStack) {
      const cards = [...container.querySelectorAll('.lanyard-builder__stack-card')];
      slotH = cards.length >= 2
        ? cards[1].getBoundingClientRect().top - cards[0].getBoundingClientRect().top
        : el.offsetHeight + 8;
    }

    this.dragState = {
      uid,
      container,
      dragEl: el,
      isStack,
      slotH,
      startY: rect.top - containerRect.top,
      offsetY: clientY - rect.top,
      currentY: rect.top - containerRect.top,
      moved: immediate,
    };

    if (immediate) {
      el.classList.add('lanyard-builder__layer-wrapper--dragging', 'lanyard-builder__stack-card--dragging');
    }

    document.addEventListener('pointermove', this.#onDragMove);
    document.addEventListener('pointerup', this.#endDrag);
    document.addEventListener('pointercancel', this.#endDrag);
  }

  // Clamp a drop target so invariants hold while dragging: top loops stay
  // pinned at the top (returns null when the top loop itself is dragged),
  // nothing may be dropped above a top loop, and tails stay at the bottom.
  #clampStackTargetIndex(index, draggedUid) {
    const draggedRole = this.#entry(draggedUid)?.role;
    if (draggedRole === 'toploop') return null;

    const rest = this.stack.filter((id) => id !== draggedUid);
    const topCount = rest.filter((id) => this.#entry(id)?.role === 'toploop').length;
    const tailCount = rest.filter((id) => this.#entry(id)?.role === 'tail').length;

    let min = topCount;
    let max = rest.length;
    if (draggedRole === 'tail') {
      min = Math.max(min, rest.length - tailCount);
    } else {
      max = rest.length - tailCount;
    }
    return Math.min(Math.max(index, min), max);
  }

  #onDragMove = (event) => {
    if (!this.dragState) return;
    const clientY = event.clientY ?? event.touches?.[0]?.clientY;
    if (clientY == null) return;

    const { container, dragEl, isStack } = this.dragState;
    const containerRect = container.getBoundingClientRect();
    const y = clientY - containerRect.top - this.dragState.offsetY;

    if (!this.dragState.moved) {
      if (Math.abs(y - this.dragState.startY) < 5) return;
      this.dragState.moved = true;
      dragEl.classList.add('lanyard-builder__layer-wrapper--dragging', 'lanyard-builder__stack-card--dragging');
    }

    this.dragState.currentY = y;

    if (isStack) {
      dragEl.style.transform = `translateY(${y - this.dragState.startY}px)`;
      const targetIndex = this.#clampStackTargetIndex(this.#stackDragTargetIndex(), this.dragState.uid);
      if (targetIndex == null) return;
      this.#shiftOtherCards(targetIndex);
    } else {
      dragEl.style.top = `${y}px`;
      const targetIndex = this.#clampStackTargetIndex(this.#dragTargetIndex(y, containerRect.height), this.dragState.uid);
      if (targetIndex == null) return;
      this.#shiftOtherLayers(this.dragState.uid, targetIndex, containerRect.height);
    }
  };

  #endDrag = () => {
    if (!this.dragState) return;
    document.removeEventListener('pointermove', this.#onDragMove);
    document.removeEventListener('pointerup', this.#endDrag);
    document.removeEventListener('pointercancel', this.#endDrag);

    const { uid, isStack, currentY, container } = this.dragState;

    if (!this.dragState.moved) {
      this.dragState = null;
      this.#selectEntry(uid);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const targetIndex = this.#clampStackTargetIndex(
      isStack ? this.#stackDragTargetIndex() : this.#dragTargetIndex(currentY, containerRect.height),
      uid
    );
    const oldIndex = this.stack.indexOf(uid);

    if (targetIndex != null && targetIndex !== oldIndex && targetIndex >= 0) {
      const [removed] = this.stack.splice(oldIndex, 1);
      this.stack.splice(targetIndex, 0, removed);
    }

    this.dragState = null;
    this.render();
  };

  // Fixed-slot target index for dragging cards in the stack column.
  #stackDragTargetIndex() {
    const { container, dragEl, currentY } = this.dragState;
    const dragMid = container.getBoundingClientRect().top + currentY + dragEl.offsetHeight / 2;
    let target = 0;
    for (const card of container.querySelectorAll('.lanyard-builder__stack-card')) {
      if (card === dragEl) continue;
      const rect = card.getBoundingClientRect();
      if (dragMid > rect.top + rect.height / 2) target++;
    }
    return target;
  }

  // While dragging a stack card, shift the other cards toward their new slots
  // so the list animates open where the dragged card will drop.
  #shiftOtherCards(targetIndex) {
    const { container, dragEl, uid, slotH } = this.dragState;

    const newStack = [...this.stack];
    const oldIndex = newStack.indexOf(uid);
    if (oldIndex < 0) return;
    newStack.splice(oldIndex, 1);
    newStack.splice(targetIndex, 0, uid);

    const newIndex = new Map();
    newStack.forEach((id, i) => newIndex.set(id, i));

    for (const card of container.querySelectorAll('.lanyard-builder__stack-card')) {
      const cardUid = Number(card.dataset.uid);
      if (cardUid === uid) continue;
      const next = newIndex.get(cardUid);
      if (next == null) continue;
      const delta = (next - this.stack.indexOf(cardUid)) * slotH;
      card.classList.add('lanyard-builder__stack-card--shifting');
      card.style.transform = `translateY(${delta}px)`;
    }
  }

  // Where the dragged item should land within the stack, based on its current
  // vertical position inside the stage.
  #dragTargetIndex(y, stageH) {
    const s = this.#effectiveScale();
    const totalH = this.#stackTotalHeight(s);
    const startY = Math.max(0, (stageH - totalH) / 2);

    let targetIndex = 0;
    let acc = startY;
    for (const uid of this.stack) {
      if (uid === this.dragState?.uid) continue;
      const entry = this.#entry(uid);
      const h = this.#entryHeightCm(entry) * s;
      const overlap = entry ? this.#overlapPx(entry.role) : 0;
      const nextY = acc + h - overlap;
      const midY = acc + (h - overlap) / 2;
      if (y < midY) break;
      targetIndex++;
      acc = nextY;
    }
    return targetIndex;
  }

  // While dragging, move the other layers toward their new positions so the
  // stack animates open where the dragged item will drop.
  #shiftOtherLayers(dragUid, targetIndex, stageH) {
    const s = this.#effectiveScale();
    const totalH = this.#stackTotalHeight(s);
    const startY = Math.max(0, (stageH - totalH) / 2);

    const newStack = [...this.stack];
    const oldIndex = newStack.indexOf(dragUid);
    if (oldIndex < 0) return;
    newStack.splice(oldIndex, 1);
    newStack.splice(targetIndex, 0, dragUid);

    const tops = new Map();
    let cursor = startY;
    for (const uid of newStack) {
      const entry = this.#entry(uid);
      const h = this.#entryHeightCm(entry) * s;
      const overlap = entry ? this.#overlapPx(entry.role) : 0;
      tops.set(uid, cursor);
      cursor += h - overlap;
    }

    for (const child of this.refs.stage.querySelectorAll('.lanyard-builder__layer-wrapper')) {
      const childUid = Number(child.dataset.uid);
      if (childUid === dragUid) continue;
      const newTop = tops.get(childUid);
      if (newTop != null) {
        child.classList.add('lanyard-builder__layer-wrapper--shifting');
        child.style.top = `${newTop}px`;
      }
    }
  }

  // ------------------------------------------------------------ stack panel

  renderStackPanel() {
    const wrap = this.refs.stack;
    wrap.innerHTML = '';

    const list = document.createElement('ol');
    list.className = 'lanyard-builder__stack-list';

    const roleLabel = { knot: 'Knot', beads: 'Beads', tail: 'Tail', decoration: 'Spacer', toploop: 'Loop' };

    this.stack.forEach((uid) => {
      const entry = this.#entry(uid);
      if (!entry) return;

      const canSwap = entry.role !== 'knot';
      const canDelete = entry.role !== 'toploop';

      const card = document.createElement('li');
      card.className = 'lanyard-builder__stack-card';
      card.dataset.uid = String(uid);
      if (this.selectedUid === uid) card.dataset.selected = 'true';

      const label = document.createElement('span');
      label.className = 'lanyard-builder__stack-card__label';
      label.textContent = roleLabel[entry.role] || entry.role;
      card.appendChild(label);

      const name = document.createElement('span');
      name.className = 'lanyard-builder__stack-card__name';
      name.textContent = entry.item.title;
      card.appendChild(name);

      if (canSwap || canDelete) {
        const actions = document.createElement('div');
        actions.className = 'lanyard-builder__stack-card__actions';

        if (canSwap) {
          const changeBtn = document.createElement('button');
          changeBtn.type = 'button';
          changeBtn.className = 'lanyard-builder__stack-card__action';
          changeBtn.setAttribute('aria-label', `Change ${entry.role}`);
          changeBtn.textContent = '⇄';
          changeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.#openGroup(entry.role, uid);
          });
          actions.appendChild(changeBtn);
        }

        if (canDelete) {
          const deleteBtn = document.createElement('button');
          deleteBtn.type = 'button';
          deleteBtn.className = 'lanyard-builder__stack-card__action';
          deleteBtn.setAttribute('aria-label', 'Remove');
          deleteBtn.textContent = '×';
          deleteBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.#removeEntry(uid);
            this.render();
          });
          actions.appendChild(deleteBtn);
        }

        card.appendChild(actions);
      }

      card.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button')) return;
        this.#startDrag(uid, event, list);
      });

      list.appendChild(card);
    });

    wrap.appendChild(list);
  }

  // ----------------------------------------------------------------- totals

  updateTotal() {
    const rope = this.#entriesByRole('rope')[0];
    let cents = 0;

    for (const entry of this.entries) {
      cents += Number(entry.item.priceCents) || 0;
    }
    if (this.labor.variantId) {
      cents += Number(this.labor.priceCents) || 0;
    }

    this.refs.total.textContent = this.#format(cents);

    // Selections beyond their variant's stock make the whole bundle
    // impossible to submit (cart/add rejects the line), so block the button
    // and say which items the customer must remove or replace. The picker
    // already prevents picking past stock, so this only triggers from stale
    // shared links or saved designs.
    const overStock = this.#overStockSelections();
    const soldOutTitles = overStock.map((group) => group.item.title);
    const note = this.refs.soldOutNote;
    if (note) {
      if (soldOutTitles.length) {
        note.textContent = `Sold out: ${soldOutTitles.join(', ')} — please remove and choose another component.`;
        note.hidden = false;
      } else {
        note.hidden = true;
      }
    }

    const canAdd = Boolean(rope && this.labor.variantId) && overStock.length === 0;
    if (this.#adding) {
      this.refs.addButton.disabled = true;
      return;
    }
    this.refs.addButton.disabled = !canAdd;
    if (!canAdd && !rope) {
      this.refs.addButtonText.textContent = 'Choose a rope';
    } else if (!canAdd && !this.labor.variantId) {
      this.refs.addButtonText.textContent = 'Assembly fee not configured';
    } else if (!canAdd && overStock.length) {
      this.refs.addButtonText.textContent = 'Remove sold-out items';
    } else {
      this.refs.addButtonText.textContent = 'Add to cart';
    }
  }

  // Selections that exceed their variant's stock. The picker blocks picking
  // past stock, so this can only happen through a stale shared link or a
  // saved design whose stock has since dropped.
  #overStockSelections() {
    const grouped = new Map();
    for (const entry of this.entries) {
      const key = this.#itemKey(entry.item);
      const group = grouped.get(key) || { item: entry.item, qty: 0 };
      group.qty += 1;
      grouped.set(key, group);
    }
    return [...grouped.values()].filter((group) => {
      const stock = group.item.inventory == null ? -1 : Number(group.item.inventory);
      return stock >= 0 && group.qty > stock;
    });
  }

  // -------------------------------------------------------------- add to cart

  async addToCart() {
    const rope = this.#entriesByRole('rope')[0];
    if (!rope) {
      this.#setMessage('Please choose a rope first.', true);
      return;
    }
    if (!this.labor.variantId) {
      this.#setMessage('Assembly fee product is not configured.', true);
      return;
    }
    if (this.#overStockSelections().length) {
      this.#setMessage('A component in this design is sold out. Remove it to add to cart.', true);
      return;
    }

    this.#adding = true;
    this.refs.addButton.disabled = true;
    this.refs.addButton.dataset.loading = 'true';
    this.refs.addButtonText.textContent = 'Adding…';
    this.#setMessage('Adding to cart…', false);

    let deferred = null;
    try {
      const hash = this.#serializeHash();
      const builderUrl = `${window.location.origin}/products/custom-lanyard-builder${hash ? `#${hash}` : ''}`;
      const bundleId = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, '').slice(0, 18)
        : `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 9)}`.slice(0, 18);

      // Merge identical component instances into a single line per variant.
      // Free components (rope/knot/ball-knot) have no variantId — they are not
      // cart lines; they live in the order spec instead.
      const grouped = new Map();
      for (const entry of this.entries) {
        if (!entry.item.variantId) continue;
        const key = String(entry.item.variantId);
        if (!grouped.has(key)) {
          grouped.set(key, { entry, quantity: 0 });
        }
        grouped.get(key).quantity += 1;
      }

      const items = [];
      const roleLabels = {
        rope: 'Color',
        knot: 'Knot',
        beads: 'Beads',
        tail: 'Tail',
        decoration: 'Spacer',
      };
      for (const group of this.groups) {
        if (!roleLabels[group.role]) roleLabels[group.role] = group.label;
      }

      for (const { entry, quantity } of grouped.values()) {
        const properties = {
          Component: roleLabels[entry.role] || entry.role,
          'Bundle ID': bundleId,
          Preview: builderUrl,
        };
        if (entry.role === 'tail') {
          if (this.#isLetterTail(entry.item)) {
            if (this.tailLetters) properties['Custom letters'] = this.tailLetters;
          } else if (this.tailInput) {
            properties['Custom text'] = this.tailInput;
          }
        }
        items.push({
          id: entry.item.variantId,
          quantity,
          properties,
        });
      }

      // The assembly fee carries the shareable spec hash and a link back to
      // the builder; the hash fully describes the configuration.
      const laborProperties = {
        Component: 'Assembly',
        'Bundle ID': bundleId,
        Preview: builderUrl,
      };
      items.push({ id: this.labor.variantId, quantity: 1, properties: laborProperties });

      deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((it) => ({ merchandiseId: it.id, quantity: it.quantity })),
          promise: deferred.promise,
        })
      );

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections_url: window.location.pathname }),
      });

      const cart = await response.json();

      if (!response.ok || cart.status) {
        deferred.reject(new Error(cart.description || cart.message || 'Add to cart failed'));
        this.#setMessage(cart.description || cart.message || 'Something went wrong.', true);
        return;
      }

      const itemCount = cart.item_count ?? 0;
      // Shopify's standard-events helper may call Intl.NumberFormat with the
      // cart currency; ensure it is present so the drawer does not throw
      // "Currency code is required with currency style".
      if (!cart.currency) cart.currency = this.currency || 'USD';
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { itemCount, source: 'lanyard-builder', didError: false },
      });

      this.#setMessage('Added to cart ✓', false);

      // The theme only auto-opens the drawer when its global
      // `auto_open_cart_drawer` setting is on; open it explicitly here.
      const drawer = document.getElementById('cart-drawer');
      if (drawer && !drawer.isOpen) drawer.open();
    } catch (error) {
      console.error('[lanyard-builder]', error);
      deferred?.reject(error);
      this.#setMessage('Something went wrong. Please try again.', true);
    } finally {
      this.#adding = false;
      delete this.refs.addButton.dataset.loading;
      this.updateTotal();
    }
  }

  #setMessage(text, isError) {
    this.refs.message.textContent = text;
    this.refs.message.dataset.error = String(isError);
  }

  // ------------------------------------------------------------------ share

  // Share the composited preview image through the native share sheet when the
  // browser supports file sharing; otherwise download the PNG so it can be
  // posted manually.
  async #shareImage() {
    this.#setMessage('Preparing image…', false);
    const dataUrl = await this.#capturePreview(2);
    if (!dataUrl) {
      this.#setMessage('Could not generate the preview image.', true);
      return;
    }

    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], 'edcforce-lanyard.png', { type: 'image/png' });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: document.title,
          text: 'My custom EDCFORCE paracord lanyard',
          url: window.location.href,
          files: [file],
        });
        this.#setMessage('Shared ✓', false);
      } catch (error) {
        if (error?.name !== 'AbortError') this.#setMessage('Share failed. Please try again.', true);
      }
      return;
    }

    this.#setMessage('Web Share not supported — use Download image instead.', true);
  }

  async #downloadImage() {
    this.#setMessage('Preparing image…', false);
    const dataUrl = await this.#capturePreview(2);
    if (!dataUrl) {
      this.#setMessage('Could not generate the preview image.', true);
      return;
    }

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = 'edcforce-lanyard.png';
    link.click();
    this.#setMessage('Image downloaded ✓', false);
  }

  async #copyShareLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      this.#setMessage('Link copied ✓', false);
    } catch {
      this.#setMessage('Copy failed — copy the address bar manually.', true);
    }
  }

  // ------------------------------------------------------------ image capture

  async #capturePreview(scaleFactor = 1) {
    const stage = this.refs.stage;
    const cssWidth = Math.max(1, stage.clientWidth);
    const cssHeight = Math.max(1, stage.clientHeight);
    const width = Math.round(cssWidth * scaleFactor);
    const height = Math.round(cssHeight * scaleFactor);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.scale(scaleFactor, scaleFactor);
    // Fill a white background so the PNG does not render on transparency
    // (which many social apps display as black).
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, cssWidth, cssHeight);

    const layers = /** @type {HTMLElement[]} */ ([...stage.querySelectorAll('.lanyard-builder__rope, .lanyard-builder__layer-wrapper')])
      .filter((el) => !el.classList.contains('lanyard-builder__layer-wrapper--dragging'))
      .sort((a, b) => Number(a.style.zIndex) - Number(b.style.zIndex));
    if (layers.length === 0) return null;

    try {
      // Preload source images by URL (sprite sheets are shared across layers).
      const imageByUrl = new Map();
      const load = (url) => {
        if (imageByUrl.has(url)) return imageByUrl.get(url);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        const promise = new Promise((res, rej) => {
          img.onload = () => res(img);
          img.onerror = () => rej(new Error('image load failed'));
        });
        img.src = url;
        imageByUrl.set(url, promise);
        return promise;
      };

      for (const layer of layers) {
        if (layer.classList.contains('lanyard-builder__rope')) {
          const ropeBg = this.#ropeBackground();
          if (ropeBg) {
            const img = await load(ropeBg);
            const rect = layer.getBoundingClientRect();
            const stageRect = stage.getBoundingClientRect();
            const x = rect.left - stageRect.left;
            const y = rect.top - stageRect.top;
            // Render the rope to a temporary canvas so we can fade its top and
            // bottom edges; this prevents the rope from peeking out behind the
            // top loop or tail in the captured image.
            const ropeCanvas = document.createElement('canvas');
            ropeCanvas.width = Math.max(1, Math.ceil(rect.width));
            ropeCanvas.height = Math.max(1, Math.ceil(rect.height));
            const rctx = ropeCanvas.getContext('2d');
            if (rctx) {
              const tileH = 1024;
              for (let yy = 0; yy < rect.height; yy += tileH) {
                rctx.drawImage(img, 477, 68, rect.width, tileH, 0, yy, rect.width, tileH);
              }
              const fade = 14;
              const grad = rctx.createLinearGradient(0, 0, 0, rect.height);
              grad.addColorStop(0, 'rgba(0,0,0,0)');
              grad.addColorStop(Math.min(1, fade / rect.height), 'rgba(0,0,0,1)');
              grad.addColorStop(Math.max(0, 1 - fade / rect.height), 'rgba(0,0,0,1)');
              grad.addColorStop(1, 'rgba(0,0,0,0)');
              rctx.globalCompositeOperation = 'destination-in';
              rctx.fillStyle = grad;
              rctx.fillRect(0, 0, rect.width, rect.height);
              rctx.globalCompositeOperation = 'source-over';
              ctx.drawImage(ropeCanvas, x, y);
            }
          }
          continue;
        }

        const uid = Number(layer.dataset.uid);
        const entry = this.#entry(uid);
        if (!entry || !entry.item.image) continue;

        const rect = layer.getBoundingClientRect();
        const stageRect = stage.getBoundingClientRect();
        const x = rect.left - stageRect.left;
        const y = rect.top - stageRect.top;

        if (this.#isSprite(entry.item)) {
          const img = await load(entry.item.image);
          const cols = Number(entry.item.spriteColumns) || 1;
          const frames = Number(this.data.ropeCount) || cols;
          const rows = Math.max(1, Math.ceil(frames / cols));
          const idx = this.#currentRopeFrame();
          const col = idx % cols;
          const row = Math.floor(idx / cols);
          const fw = img.naturalWidth / cols;
          const fh = img.naturalHeight / rows;
          if (entry.role === 'toploop') {
            // Capture only the revealed top portion of the full-size loop.
            const fullH = rect.height * (TOPLOOP_FULL_HEIGHT_CM / this.toploopHeight);
            ctx.save();
            ctx.beginPath();
            ctx.rect(x, y, rect.width, rect.height);
            ctx.clip();
            ctx.drawImage(img, col * fw, row * fh, fw, fh, x, y, rect.width, fullH);
            ctx.restore();
          } else {
            ctx.drawImage(img, col * fw, row * fh, fw, fh, x, y, rect.width, rect.height);
          }
        } else if (entry.role === 'toploop' && entry.item.extensionImage) {
          // Metal ring: small ring first, then the braid in front of its lower
          // half starting at the ring's vertical middle — mirrors the DOM
          // composite in renderPreview.
          const ringImg = await load(entry.item.image);
          ctx.drawImage(ringImg, x + TOPLOOP_RING_OFFSET_PCT * rect.width, y, rect.width, rect.width);
          const img = await load(entry.item.extensionImage);
          const cols = Number(entry.item.extensionColumns) || 1;
          const frames = Number(this.data.ropeCount) || cols;
          const rows = Math.max(1, Math.ceil(frames / cols));
          const idx = this.#currentRopeFrame();
          const fw = img.naturalWidth / cols;
          const fh = img.naturalHeight / rows;
          const fullH = rect.height * (TOPLOOP_FULL_HEIGHT_CM / this.toploopHeight);
          const pxPerCm = rect.width / (Number(entry.item.widthCm) || 0.53);
          const braidW = this.#toploopBraidWidthCm() * pxPerCm;
          ctx.save();
          ctx.beginPath();
          ctx.rect(x, y, rect.width, rect.height);
          ctx.clip();
          ctx.drawImage(
            img,
            (idx % cols) * fw,
            Math.floor(idx / cols) * fh,
            fw,
            fh,
            x + (rect.width - braidW) / 2,
            y + rect.width / 2,
            braidW,
            fullH
          );
          ctx.restore();
        } else {
          const img = await load(entry.item.image);
          const aspect = img.naturalWidth / img.naturalHeight || 1;
          const drawW = rect.width;
          const drawH = drawW / aspect;
          const drawY = y + (rect.height - drawH) / 2;
          ctx.drawImage(img, x, drawY, drawW, drawH);
        }
      }

      return canvas.toDataURL('image/png');
    } catch (error) {
      console.warn('[lanyard-builder] Preview capture skipped:', error);
      return null;
    }
  }
}

if (!customElements.get('lanyard-builder-component')) {
  customElements.define('lanyard-builder-component', LanyardBuilder);
}

